'use client'

import { Avatar, Button, ChevronLeft, ExternalLink, Tooltip, useIsMobile } from '@chain-zen/ui'

import { WORKSPACE_ROUTES } from '../../../../constants/routes'
import type { IShipment } from '../../../../types/shipment'
import { ShipmentCreatedViaEnum } from '../../../../types/shipment'
import { ShipmentNumber } from '../shipment-header/shipment-number'

interface IInboxShipmentHeaderProps {
  isShipmentOwner: boolean
  onBack?: () => void
  shipment: IShipment
  shipmentID: string
}

/**
 * This component is used to display the header of a shipment.
 * It displays the shipment number, the shipment owner, and the workspace.
 * It also provides actions to view the shipment in the booking, view in TMS, share with customer, copy tracking URL, and open shipment in a new tab.
 */
const InboxShipmentHeader = ({ shipmentID, shipment, onBack }: IInboxShipmentHeaderProps) => {
  const isShipmentOwner = shipment?.isShipmentOwner || false
  const shipmentOwnerWorkspace = shipment?.shipmentOwnerWorkspace
  const createdVia = shipment?.createdVia
  const shipmentNumber = shipment?.shipmentNumber || ''
  const tmsURL = shipment?.tmsURL ?? ''
  const showOpenOnNewTab = true
  const isDesktop = !useIsMobile()

  return (
    <div className="flex h-[72px] items-center justify-between border-b px-4">
      {/* Left side - Company info and shipment number */}
      <div className="flex min-w-0 items-center gap-2">
        {!!onBack && (
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 rounded-md" onClick={onBack}>
            <span className="sr-only">Back</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        {/* Company info with back button */}
        <Tooltip content={shipmentOwnerWorkspace?.displayName || ''} side="bottom">
          <Avatar
            src={shipmentOwnerWorkspace?.logo || undefined}
            alt={shipmentOwnerWorkspace?.displayName || ''}
            name={shipmentOwnerWorkspace?.displayName || ''}
            size="sm"
            variant={'square'}
          />
        </Tooltip>
        <span className="ml-2 text-muted-foreground font-bold">›</span>
        <div className="flex-1">
          {/* Shipment Number */}
          <ShipmentNumber
            shipmentID={shipmentID}
            shipmentOwnerWorkspaceId={shipmentOwnerWorkspace?.id ?? ''}
            shipmentNumber={shipmentNumber}
            canEdit={createdVia !== ShipmentCreatedViaEnum.Integration && isShipmentOwner}
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        {!!isDesktop && !!tmsURL && (
          <Button variant="outline" size="sm" className="h-8">
            <a href={tmsURL} target="_blank" rel="noopener noreferrer">
              View in TMS
            </a>
          </Button>
        )}

        {!!showOpenOnNewTab && (
          <Tooltip content="Open shipment in new tab" side="bottom">
            <a
              href={WORKSPACE_ROUTES.SINGLE_SHIPMENT(shipmentOwnerWorkspace?.workspacename ?? '', shipmentID)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export { InboxShipmentHeader }
